import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight, AudioWaveform, Moon, Volume2, VolumeX, Zap } from "lucide-react";
import { WarmBreeze } from "@/WarmBreeze";
import { ColourBloom } from "@/ColourBloom";
import { SnakeScore, ScaryFlash } from "@/SnakeScore";
import { ModeList } from "@/ModeList";
import { startLullaby } from "@/lullaby";
import { SleepFade, SleepPanel, NIGHT_RAIN } from "@/SleepFade";
import { DawnGlow } from "@/Dawn";
import { useNightRainThunder } from "@/RainLayer";
import { playCrashRain } from "@/rainSound";
import { CookieNote } from "@/CookieNote";
import { DroneShade } from "@/DroneShade";
import { useTronHum } from "@/tronHum";
import { VisitorCount } from "@/VisitorCount";
import { humFlags } from "@/humSurprise";
import { playRandomNoise } from "@/noise";
import { ACID_RAID_URL, ACID_SITE_URL, INSOMNIA_HELP_URL, ZI_SITE_URL } from "@/config";
import { AudioCluster } from "@/AudioCluster";
import { AudioToggle } from "@/AudioToggle";
import { RaveModeButton } from "@/RaveModeButton";
import { SecretLink, useSecret } from "@/SecretLink";
import { PlaylistPanel } from "@/PlaylistPanel";
import { SleepStory } from "@/SleepStory";
import { SLEEP_PLAYLIST } from "@/SpotifyPlayer";
import { EFFECTS, ACID_MODES } from "@/effects";
import { useCanvasEffect } from "@/useCanvasEffect";
import { pointer, rave, mouthStyle } from "@/vessels";
import { useHum } from "@/useHum";
import { getAudio } from "@/audio";
import { HumStartButton } from "@/HumStartButton";
import { useAcidSounds } from "@/useAcidSounds";
import { useMayday } from "@/mayday";
import { useAmbientBeat } from "@/ambientBeat";
import { useMemory } from "@/useMemory";
import { api, useAuth } from "@/auth";
import { SecretGate } from "@/SecretGate";
import { useHiddenTriggers } from "@/useHiddenTriggers";

const EFFECT_KEY = "closed-eyes-settings";
const URI_KEY = "closed-eyes-active-uri";

const loadIndex = () => {
  const tron = EFFECTS.findIndex((e) => e.id === "tron");
  try {
    const id = JSON.parse(localStorage.getItem(EFFECT_KEY))?.effectId;
    const i = EFFECTS.findIndex((e) => e.id === id);
    return ACID_MODES.includes(id) || i < 0 ? tron : i;
  } catch {
    return tron;
  }
};

export const Stage = () => {
  const canvasRef = useRef(null);
  const fishRef = useRef(null);
  const spotifyRef = useRef(null);
  const pressureRef = useRef(0);
  const speedRef = useRef(1);
  const [index, setIndex] = useState(loadIndex);
  const [idle, setIdle] = useState(false);
  const [hum, setHum] = useState(false);
  const [acidOn, setAcidOn] = useState(false);
  const [acidUnlocked, setAcidUnlocked] = useState(false);
  const [sleepOn, setSleepOn] = useState(false);
  const [sleepMins, setSleepMinsRaw] = useState(() => Number(localStorage.getItem("sleep-site-fade-mins")) || 30);
  const setSleepMins = useCallback((m) => { setSleepMinsRaw(m); localStorage.setItem("sleep-site-fade-mins", String(m)); }, []);
  const [sleepLeft, setSleepLeft] = useState(0);
  const [crashFx, setCrashFx] = useState({ exploding: false, crashing: false });
  const [beatOn, setBeatOn] = useState(() => localStorage.getItem("sleep-site-beat") !== "0");
  const toggleBeat = () => setBeatOn((b) => { localStorage.setItem("sleep-site-beat", b ? "0" : "1"); return !b; });
  const sleep = sleepOn;
  const [dawn, setDawn] = useState(0);
  const wasAsleep = useRef(false);
  useEffect(() => {
    if (wasAsleep.current && !sleepOn) setDawn((d) => d + 1);
    wasAsleep.current = sleepOn;
  }, [sleepOn]);
  const endDawn = useCallback(() => setDawn(0), []);
  const [musicOn, setMusicOn] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setMusicOn(!!spotifyRef.current?.__playing), 700);
    return () => clearInterval(id);
  }, []);
  const acidRef = useRef(false);
  acidRef.current = acidUnlocked;
  const [isFull, setIsFull] = useState(false);
  const toggleFull = () => {
    const p = document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.();
    p?.catch(() => {});
  };
  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const [activeUri, setActiveUri] = useState(() => localStorage.getItem(URI_KEY) || SLEEP_PLAYLIST);
  const effect = EFFECTS[index];
  mouthStyle.bubbly = effect.id === "dream";
  const secret = useSecret();
  useCanvasEffect(canvasRef, effect, pressureRef, speedRef, fishRef);
  useHum(hum);
  useAcidSounds(acidOn);
  useMayday(effect.id === "mayday" && acidUnlocked);
  useAmbientBeat(beatOn);
  useTronHum(effect.id === "tron" && !sleepOn);
  useNightRainThunder(activeUri === NIGHT_RAIN && musicOn);
  const [humBlocked, setHumBlocked] = useState(false);
  useEffect(() => {
    const { ctx } = getAudio();
    const sync = () => {
      const running = ctx.state === "running";
      setHumBlocked(!running);
      if (!running) return;
      setHum(true);
      ctx.removeEventListener("statechange", sync);
    };
    const t = setTimeout(sync, 400);
    ctx.addEventListener("statechange", sync);
    return () => { clearTimeout(t); ctx.removeEventListener("statechange", sync); };
  }, []);
  const startHum = () => { getAudio(); setHum(true); setHumBlocked(false); };
  const memory = useMemory(canvasRef, fishRef);
  const auth = useAuth();
  const [gate, setGate] = useState(null);
  const unlocked = !!auth.user?.unlocked;
  const triggers = useHiddenTriggers({
    onAcid: () => {
      if (sleep) return;
      setAcidUnlocked(true);
      secret.reveal();
    },
    onMemory: () => (unlocked ? memory.record() : setGate("locked")),
  });

  useEffect(() => {
    const { pathname, search } = window.location;
    if (!pathname.startsWith("/payment/")) return;
    const sessionId = new URLSearchParams(search).get("session_id");
    window.history.replaceState(null, "", "/");
    if (!pathname.includes("success") || !sessionId) { setGate("locked"); return; }
    setGate("checking");
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      const r = await api(`/payments/status/${sessionId}`).catch(() => null);
      if (r && r.status === 404) { setGate("failed"); return; }
      const d = r && r.ok ? await r.json() : null;
      if (d?.payment_status === "paid") { await auth.refresh(); setGate("thanks"); return; }
      if (d && ["expired", "failed"].includes(d.payment_status)) { setGate("failed"); return; }
      if (++tries < 15) setTimeout(poll, 2000);
      else setGate("failed");
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let quake, blood;
    const onCrash = () => {
      clearTimeout(quake);
      clearTimeout(blood);
      setCrashFx({ exploding: true, crashing: true });
      quake = setTimeout(() => setCrashFx((c) => ({ ...c, exploding: false })), 900);
      blood = setTimeout(() => setCrashFx({ exploding: false, crashing: false }), 5600);
      startLullaby();
      playCrashRain();
    };
    window.addEventListener("snake-crash", onCrash);
    return () => { clearTimeout(quake); clearTimeout(blood); window.removeEventListener("snake-crash", onCrash); };
  }, []);
  useEffect(() => { rave.on = secret.raving; }, [secret.raving]);
  useEffect(() => {
    humFlags.quiet = sleep;
    if (!sleep) return;
    setAcidOn(false);
    setAcidUnlocked(false);
    secret.calm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleep]);
  useEffect(() => {
    if (!acidUnlocked && ACID_MODES.includes(effect.id)) setIndex(0);
  }, [acidUnlocked, effect.id]);
  useEffect(() => {
    localStorage.setItem(EFFECT_KEY, JSON.stringify({ effectId: effect.id }));
  }, [effect.id]);

  const next = useCallback(() => setIndex((i) => {
    let n = (i + 1) % EFFECTS.length;
    while (!acidRef.current && ACID_MODES.includes(EFFECTS[n].id)) n = (n + 1) % EFFECTS.length;
    return n;
  }), []);

  const playUri = (uri) => {
    const c = spotifyRef.current;
    if (!c) return;
    setActiveUri(uri);
    localStorage.setItem(URI_KEY, uri);
    try {
      c.loadUri(uri);
      setTimeout(() => {
        try { c.play(); } catch { /* not loaded yet */ }
      }, 900);
    } catch { /* controller not ready */ }
  };

  useEffect(() => {
    let timer;
    const wake = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(function idleCheck() {
        if (document.querySelector(".audio-cluster:hover, .playlist-panel:focus-within")) timer = setTimeout(idleCheck, 1000);
        else setIdle(true);
      }, 3000);
    };
    const onKey = (e) => {
      wake();
      if (e.ctrlKey || e.metaKey || e.altKey || document.querySelector('[role="dialog"]')) return;
      if (e.code === "Space") { e.preventDefault(); if (!e.repeat && acidRef.current) setAcidOn((a) => !a); }
    };
    const onBlur = () => setTimeout(() => {
      if (document.activeElement?.tagName === "IFRAME") document.activeElement.blur();
    }, 400);
    const track = (e) => { pointer.x = e.clientX; pointer.y = e.clientY; pointer.last = performance.now(); };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("touchstart", wake);
    window.addEventListener("pointermove", track);
    window.addEventListener("pointerdown", track);
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("touchstart", wake);
      window.removeEventListener("pointermove", track);
      window.removeEventListener("pointerdown", track);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, [next]);

  const cls = ["stage", "dark", effect.id === "dream" && "is-dream", idle && "is-idle", sleep && "is-sleeping", secret.raving && "is-raving", crashFx.exploding && "is-exploding", crashFx.crashing && "is-crashing"].filter(Boolean).join(" ");

  return (
    <main className={cls} data-testid="visualiser-stage">
      <canvas ref={canvasRef} className="stage-canvas" data-testid="canvas-experience" />
      <div className="vignette" />
      <div className="grain" />
      <div key={effect.id} className="effect-flash" data-testid="effect-flash" data-effect={effect.id} />
      {secret.raving && <div key={`rave-${secret.hits}`} className="rave-layer" data-testid="rave-overlay"><div className="rave-flash" data-testid="rave-flash" /></div>}
      {secret.open && <SecretLink onClose={secret.close} />}
      {memory.recording && (
        <div className="memory-indicator" data-testid="memory-indicator"><span className="rec-dot" /> {memory.left}</div>
      )}
      <SecretGate state={gate} user={auth.user} onOpenChange={(o) => !o && setGate(null)} />
      <WarmBreeze />
      <ColourBloom />
      <canvas ref={fishRef} className="fish-canvas" data-testid="fish-canvas" />
      <DroneShade />
      <ScaryFlash />
      {effect.id === "snake" && <SnakeScore />}
      {sleep && <SleepFade mins={sleepMins} spotifyRef={spotifyRef} onTick={setSleepLeft} onWake={() => setSleepOn(false)} />}
      {dawn > 0 && !sleep && <DawnGlow key={`dawn-${dawn}`} onDone={endDawn} />}
      <SleepPanel sleep={sleep} mins={sleepMins} setMins={setSleepMins} leftMs={sleepLeft} onToggle={() => setSleepOn((s) => !s)}
        onPickPlaylist={(uri) => { playUri(uri); setSleepOn(true); }} />
      {humBlocked && <HumStartButton onStart={startHum} />}
      <CookieNote />

      {effect.id === "afx" && (
        <a className="afx-link" href={ACID_RAID_URL} target="_blank" rel="noopener noreferrer" data-testid="afx-acid-raid-link">
          acid raid <ArrowUpRight size={14} />
        </a>
      )}
      <a className="label-name title-main acid-site-link" href={ACID_SITE_URL} target="_blank" rel="noopener noreferrer" data-testid="acid-site-link">acid site</a>
      <a className="label-name title-main acid-site-link zi-site-link" href={ZI_SITE_URL} target="_blank" rel="noopener noreferrer" data-testid="zi-site-link">zi site</a>
      <header className="hud hud-top">
        <button className="fs-link" onClick={toggleFull} data-testid="fullscreen-link">
          {isFull ? "exit full screen" : "full screen"}
        </button>
      </header>

      <div className="hud hud-playlists">
        <PlaylistPanel activeUri={activeUri} onPlay={playUri} />
      </div>
      <ModeList activeId={effect.id} showAcid={acidUnlocked} onSelect={setIndex} />

      <footer className="hud hud-bottom">
        <div className="title-stack">
          <h1 className="label-name title-main" data-testid="main-title" {...triggers.titleProps}>sleep site</h1>
          <VisitorCount />
        </div>
        <div className="bottom-right">
          <div className="bottom-chips">
            <AudioToggle on={sleep} onClick={() => setSleepOn((s) => !s)} testId="sleep-button"
              ariaLabel="Sleep: no acid or loud surprises, fade to black" icon={<Moon size={14} />} label="sleep" />
            <AudioToggle on={hum} onClick={() => setHum((h) => !h)} testId="hum-button" ariaLabel="Hum forever"
              icon={hum ? <Volume2 size={14} /> : <VolumeX size={14} />} label="hum" />
            <AudioToggle on={beatOn} onClick={toggleBeat} testId="beat-button" ariaLabel="Ambient beat on or off"
              icon={<AudioWaveform size={14} />} label="beat" />
            {acidUnlocked && (
              <AudioToggle on={acidOn} testId="acid-sounds-button" ariaLabel="Random acid sounds" icon={<Zap size={14} />} label="303"
                onClick={() => { if (!acidOn) window.open(ACID_RAID_URL, "_blank", "noopener,noreferrer"); setAcidOn(!acidOn); }} />
            )}
            <RaveModeButton onClick={next} />
          </div>
          <AudioCluster controllerRef={spotifyRef} initialUri={activeUri} onWake={() => setIdle(false)} />
          <div className="footer-links">
            <SleepStory onClose={() => !sleep && playRandomNoise()} />
            {acidUnlocked && (
              <a className="help-link" href={ACID_RAID_URL} target="_blank" rel="noopener noreferrer" data-testid="acid-raid-link">
                acid raid <ArrowUpRight size={12} />
              </a>
            )}
            <a className="help-link" href={INSOMNIA_HELP_URL} target="_blank" rel="noopener noreferrer" data-testid="insomnia-help-link">
              Can't sleep? NHS insomnia help <ArrowUpRight size={12} />
            </a>
          </div>
        </div>
      </footer>
      {acidUnlocked && <a className="acid-strip" href={ACID_RAID_URL} target="_blank" rel="noopener noreferrer" aria-label="acid raid" data-testid="acid-strip-link" />}
    </main>
  );
};
